import { useEffect, useMemo } from "react";
import { z } from "zod";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { createPetSchema, type PetInput } from "@/lib/validations/pet";
import { Form } from "@/components/ui/form";
import { PetFormSection } from "./PetFormSection";
import { usePetStatuses } from "@/hooks/useLookups";
import { useDeceasedPetStatusId } from "@/lib/utils/pet-death";

interface PetFormItemProps {
  id: string;
  index: number;
  onRemove: () => void;
  onFormReady: (form: UseFormReturn<PetInput>) => void;
  schema?: z.ZodSchema; // Schema customizado opcional
  defaultRecordNumber?: string | null;
  recordNumberUi?: { disabled: boolean; placeholder: string };
}

export function PetFormItem({
  id,
  index,
  onRemove,
  onFormReady,
  schema,
  defaultRecordNumber,
  recordNumberUi,
}: PetFormItemProps) {
  const { data: statuses } = usePetStatuses();
  const { t } = useTranslation("validation");
  const deceasedStatusId = useDeceasedPetStatusId();
  const petSchemaI18n = useMemo(() => createPetSchema(t, deceasedStatusId), [t, deceasedStatusId]);

  const form = useForm<PetInput>({
    resolver: zodResolver(schema || petSchemaI18n),
    defaultValues: {
      name: "",
      owner_id: "",
      record_number: "",
      gender_id: "",
      sterilization_id: "",
      birth_date: "",
      species_id: "",
      breed_id: "",
      coat_type_id: "",
      color_id: "",
      status_id: "",
      death_date: "",
      death_cause: "",
      microchip_number: "",
      health_plan: "",
      pedigree_number: "",
      notes: "",
      allergies: [],
      temperaments: [],
      photo: null,
      attachments: [],
    },
  });

  useEffect(() => {
    if (statuses && !form.getValues("status_id")) {
      const defaultStatus = statuses.find((s) => s.name === "Ativo") || statuses[0];
      if (defaultStatus) {
        form.setValue("status_id", defaultStatus.id);
      }
    }
  }, [statuses, form]);

  useEffect(() => {
    if (!defaultRecordNumber) return;
    const currentValue = form.getValues("record_number");
    if (!currentValue) {
      form.setValue("record_number", String(defaultRecordNumber), { shouldDirty: true });
    }
  }, [defaultRecordNumber, form]);

  useEffect(() => {
    onFormReady(form);
  }, [form, onFormReady]);

  return (
    <div data-pet-id={id}>
      <Form {...form}>
        <PetFormSection
          form={form}
          index={index}
          onRemove={onRemove}
          showRemoveButton={true}
          recordNumberUi={recordNumberUi}
        />
      </Form>
    </div>
  );
}
